import React, { forwardRef } from 'react';
import { Loader2 } from 'lucide-react';
import cx from './cx';

const VARIANTS = {
  primary: 'bg-accent text-accent-fg hover:bg-accent-hover border border-transparent',
  secondary: 'bg-surface text-fg border border-line hover:bg-surface-2',
  ghost: 'bg-transparent text-fg-muted border border-transparent hover:bg-surface-2 hover:text-fg',
  danger: 'bg-loss text-loss-fg border border-transparent hover:bg-loss/90',
  'danger-ghost': 'bg-transparent text-loss border border-transparent hover:bg-loss-soft',
};

const SIZES = {
  // 36px on touch, tighter on desktop pointers.
  sm: 'h-9 gap-1 px-2.5 text-sm sm:h-7 sm:text-xs',
  md: 'h-9 gap-1.5 px-3 text-sm sm:h-8',
  lg: 'h-10 gap-2 px-4 text-sm',
};

/**
 * Buttons. `as={Link}` renders a router link styled as a button.
 * Small buttons are 36px on touch (32px from `sm`) to keep tap targets usable.
 */
export const Button = forwardRef(function Button(
  { as: Component = 'button', variant = 'secondary', size = 'md', icon: Icon, iconRight: IconRight, loading = false, disabled, fullWidth, className, children, type, ...rest },
  ref
) {
  const isButton = Component === 'button';
  return (
    <Component
      ref={ref}
      type={isButton ? type || 'button' : type}
      disabled={isButton ? disabled || loading : undefined}
      aria-disabled={!isButton && disabled ? true : undefined}
      aria-busy={loading || undefined}
      className={cx(
        'inline-flex select-none items-center justify-center whitespace-nowrap rounded-control font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant] || VARIANTS.secondary,
        SIZES[size] || SIZES.md,
        fullWidth && 'w-full',
        className
      )}
      {...rest}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : Icon && (variant === 'primary' || variant === 'danger') && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
      {children}
      {IconRight && <IconRight className="h-4 w-4 shrink-0" aria-hidden="true" />}
    </Component>
  );
});

const ICON_SIZES = { sm: 'h-9 w-9 sm:h-7 sm:w-7', md: 'h-9 w-9 sm:h-8 sm:w-8', lg: 'h-10 w-10' };

/** Icon-only button; `label` is required and becomes the accessible name and tooltip. */
export const IconButton = forwardRef(function IconButton(
  { as: Component = 'button', icon: Icon, label, variant = 'ghost', size = 'md', className, type, title, ...rest },
  ref
) {
  return (
    <Component
      ref={ref}
      type={Component === 'button' ? type || 'button' : type}
      aria-label={label}
      title={title ?? label}
      className={cx(
        'inline-flex shrink-0 items-center justify-center rounded-control transition-colors disabled:cursor-not-allowed disabled:opacity-50',
        VARIANTS[variant] || VARIANTS.ghost,
        ICON_SIZES[size] || ICON_SIZES.md,
        className
      )}
      {...rest}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
    </Component>
  );
});

export default Button;
